import React from "react";

import type { InventoryCharacteristicName } from "@/api/inventory";
import { Button } from "@/components/ui/button";
import { BottomSheet } from "@/lib/scan-ui/overlays";

/**
 * «Что не предлагать при скане» — правило СКЛАДА, а не смены.
 *
 * Зачем: на складе ФБС характеристики «ОПТ» не бывает вовсе, а штрихкод
 * тянет её вариантом и заставляет отвечать на вопрос, у которого один
 * правильный ответ. На складе МП тот же «ОПТ» законен (в снимке 22.09.2026 —
 * 6 позиций), поэтому глобального «выключить ОПТ» быть не может.
 *
 * ⚠️ Оперируем ИМЕНАМИ, не ссылками: в `Catalog_ХарактеристикиНоменклатуры`
 * живых элементов «ОПТ» ПЯТЬ, с разными `Ref_Key`, — запомненная одна ссылка
 * пропустила бы остальные четыре. Сравнение на бэке тоже по имени.
 *
 * Компонент чистый: запросы, сохранение и их ошибки живут на экране смены.
 */
export function ExcludedCharacteristicsSheet({
  warehouseName,
  names,
  excluded,
  loading,
  loadError,
  saving,
  saveError,
  onRetry,
  onSave,
  onClose,
}: {
  warehouseName: string;
  /** Живые имена характеристик 1С; `undefined` — ещё не загрузились. */
  names: InventoryCharacteristicName[] | undefined;
  /** Что выключено сейчас; `undefined` — настройка ещё не загрузилась. */
  excluded: string[] | undefined;
  loading: boolean;
  /** Сбой загрузки виден текстом: пустой список читался бы как «всё можно». */
  loadError: string | null;
  saving: boolean;
  saveError: string | null;
  onRetry: () => void;
  onSave: (names: string[]) => void;
  onClose: () => void;
}): React.ReactElement {
  // Отметки живут локально, пока их не сохранили: пока человек ставит
  // галочки, ответ сервера ещё прежний, и подмешивать его нельзя.
  const [picked, setPicked] = React.useState<string[] | null>(null);
  const chosen = picked ?? excluded ?? [];
  const chosenKeys = new Set(chosen.map(key));

  const rows = mergeRows(names, excluded);
  const ready = Boolean(names) && Boolean(excluded);

  const toggle = (name: string) => {
    const k = key(name);
    setPicked(
      chosenKeys.has(k) ? chosen.filter((n) => key(n) !== k) : [...chosen, name],
    );
  };

  return (
    <BottomSheet onClose={onClose}>
      <div className="max-h-[80dvh] overflow-y-auto p-4">
        <h3 className="text-base font-semibold">Что не предлагать при скане</h3>
        <p className="mt-1 text-xs text-slate-500">
          Отмеченные характеристики портал перестанет предлагать при скане. Правило
          запомнится для склада «{warehouseName}» и подействует и на следующую
          инвентаризацию этого склада.
        </p>

        {loadError && (
          <div className="mt-3 rounded bg-red-50 p-2 text-sm text-red-700">
            <p>{loadError}</p>
            <Button size="sm" variant="outline" className="mt-2" onClick={onRetry}>
              Повторить
            </Button>
          </div>
        )}
        {loading && !loadError && <p className="mt-3 text-sm text-slate-500">Загружаем…</p>}

        {ready && (
          <div className="mt-3 space-y-1">
            {rows.map((r, i) => (
              <React.Fragment key={r.name}>
                {/* Заголовок группы — у первой строки группы: «есть в учёте»
                    идут первыми, и граница между группами ровно одна. */}
                {(i === 0 || rows[i - 1]?.in_snapshot !== r.in_snapshot) && (
                  <GroupTitle
                    text={r.in_snapshot ? "Есть в учёте этого склада" : "Остальные характеристики 1С"}
                  />
                )}
                <label className="flex min-h-[48px] items-center gap-3 rounded-lg border p-2">
                  <input
                    type="checkbox"
                    className="size-5 shrink-0"
                    value={r.name}
                    checked={chosenKeys.has(key(r.name))}
                    onChange={() => toggle(r.name)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium">{r.name}</span>
                    {r.in_snapshot && (
                      // ⚠️ Предупреждение, а не украшение: на этом складе такое
                      // числится в учёте, и, выключив, посчитать его обычным
                      // сканом уже не выйдет — портал предложит другое.
                      <span className="block text-[11px] text-amber-700">
                        есть в учёте этого склада — выключив, обычным сканом её не посчитать
                      </span>
                    )}
                  </span>
                </label>
              </React.Fragment>
            ))}
            {rows.length === 0 && (
              <p className="text-sm text-slate-500">
                1С не вернула ни одной характеристики — выключать нечего.
              </p>
            )}
          </div>
        )}

        {saveError && (
          <p className="mt-3 rounded bg-red-50 p-2 text-sm text-red-700">{saveError}</p>
        )}

        {ready && (
          <>
            <p className="mt-3 text-xs text-slate-500">
              {chosen.length === 0
                ? "Ничего не отмечено — правило будет снято, портал предложит все варианты."
                : `Не предлагаем: ${chosen.join(", ")}.`}
            </p>
            <Button
              className="mt-2 h-12 w-full"
              disabled={saving}
              onClick={() => onSave(chosen)}
            >
              {saving ? "Сохраняем…" : "Сохранить"}
            </Button>
          </>
        )}
      </div>
    </BottomSheet>
  );
}

function GroupTitle({ text }: { text: string }): React.ReactElement {
  return <p className="pt-2 text-[11px] font-semibold uppercase text-slate-400">{text}</p>;
}

/** Сравнение имён — как на бэке: без регистра и краевых пробелов. */
function key(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Список для показа: сначала имена из учёта ЭТОГО склада, потом остальные.
 *
 * ⚠️ Уже выключенное имя, которого в 1С больше нет (переименовали, пометили
 * на удаление), в список добавляется отдельно: иначе сохранение молча сняло
 * бы правило, которого человек не трогал.
 */
function mergeRows(
  names: InventoryCharacteristicName[] | undefined,
  excluded: string[] | undefined,
): InventoryCharacteristicName[] {
  const live = names ?? [];
  const known = new Set(live.map((r) => key(r.name)));
  const lost = (excluded ?? [])
    .filter((n) => !known.has(key(n)))
    .map((n) => ({ name: n, in_snapshot: false, excluded: true }));
  // Сортировка стабильная: внутри групп остаётся порядок, в котором имена
  // пришли с сервера.
  return [...live, ...lost].sort((a, b) => Number(b.in_snapshot) - Number(a.in_snapshot));
}
